import { ViewEncapsulation } from '@angular/core';
import { ViewChild } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Output } from '@angular/core';
import { Input } from '@angular/core';
import { OnChanges } from '@angular/core';
import { Component } from '@angular/core';
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { untilDestroyed } from '@ngneat/until-destroy';
import { DateSelection } from 'app/modules/main/components/date-time-picker/date-time-picker.models';
import { AccountDatePipe } from 'app/modules/main/pipes/account-date-pipe';
import { NowProvider } from 'app/modules/main/services/now-provider.service';
import { AdvertiserTimeZoneSelectors } from 'app/modules/main/store/advertiser-time-zone/advertiser-time-zone.selectors';
import { PublisherTimeZoneSelectors } from 'app/modules/main/store/publisher-time-zone/publisher-time-zone.selectors';
import { DateTime } from 'luxon';
import { combineLatest } from 'rxjs';
import { of } from 'rxjs';
import { Observable } from 'rxjs';
import { merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-date-time-picker',
  templateUrl: './date-time-picker.component.html',
  styleUrls: ['./date-time-picker.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class DateTimePickerComponent implements OnChanges {

  @Input() openingDate: DateSelection;

  @Input() offerDatesFrom: Date;
  @Input() offerDatesTo: Date;

  @Input() readonly allowTimeComponent: boolean = true;

  @Input() readonly displayMeridian: boolean = false;
  @Input() readonly displayTimezone: boolean = false;
  @Input() readonly nowLabel: string = '';

  @Output() readonly selected = new EventEmitter<DateSelection>();
  @Output() readonly closed = new EventEmitter<DateSelection>();

  private _initialized: boolean = false;

  private _timeZone: string;
  private _timeZoneLabel: string;

  private _selection: DateSelection;

  readonly now: Date = this.nowProvider.now();

  get initialized(): boolean {
    return this._initialized;
  }

  get formattedSelection(): Observable<string> {

    if (!this.selection) {
      return of('');
    }

    if (this.selection === 'now') {
      return of(this.nowLabel);
    }

    return combineLatest([
      this.accountDatePipe.transform(this.selection, 'shortDate'),
      this.accountDatePipe.transform(this.selection, 'shortTime'),
    ])
      .pipe(
        map(([date, time]) => this.allowTimeComponent ? `${date} ${time}` : date),
        map(formattedDate => this.displayTimezone ? `${formattedDate} (${this.timeZoneLabel})` : formattedDate),
      );
  }

  get selection(): DateSelection {
    return this._selection;
  }

  get nowSelected(): boolean {
    return (this._selection === 'now');
  }

  get nowWithinDateBoundaries(): boolean {

    if (this.offerDatesFrom && this.now < this.offerDatesFrom) {
      return false;
    }

    if (this.offerDatesTo && this.now > this.offerDatesTo) {
      return false;
    }

    return true;
  }

  get timeZoneLabel(): string {
    return this._timeZoneLabel;
  }

  @ViewChild('picker', { static: true })
  picker: NgbInputDatepicker;

  pickerMinDate: NgbDateStruct;
  pickerMaxDate: NgbDateStruct;
  pickerDate: NgbDateStruct;
  pickerTime: NgbTimeStruct;

  constructor(
    private readonly accountDatePipe: AccountDatePipe,
    private readonly nowProvider: NowProvider,
    private readonly advertiserTimeZoneSelectors: AdvertiserTimeZoneSelectors,
    private readonly publisherTimeZoneSelectors: PublisherTimeZoneSelectors,
  ) {
  }

  ngOnChanges(): void {

    if (this._initialized) {
      this.applyDateBoundaries();
    }

    if (this.openingDate !== null) {

      if (this._initialized) {

        if (this.openingDate !== this._selection) {
          this.setPickerDate(this.openingDate);
        }

      } else {

        merge(
          combineLatest([
            this.advertiserTimeZoneSelectors.getTimeZone(),
            this.advertiserTimeZoneSelectors.getTimeZoneLabel(),
          ]),
          combineLatest([
            this.publisherTimeZoneSelectors.getTimeZone(),
            this.publisherTimeZoneSelectors.getTimeZoneLabel(),
          ]),
        )
          .pipe(
            untilDestroyed(this),
            take(1),
          )
          .subscribe(([timeZone, timeZoneLabel]) => {

            this._timeZone = timeZone;
            this._timeZoneLabel = timeZoneLabel;

            this.applyDateBoundaries();
            this.setPickerDate(this.openingDate);

            this._initialized = true;
          });
      }
    }
  }

  change(): void {

    if (!this.pickerDate && !this.pickerTime) {
      return;
    }

    const utcDate = this.toUtcDate(
      this.pickerDate,
      this.pickerTime,
    );

    this.pickerDate = this.fromUtcDate(utcDate);
    this.pickerTime = this.fromUtcDate(utcDate);

    this.updateSelection(utcDate);
  }

  close(): void {

    this.closed.emit(this._selection);

    this.picker.close();
  }

  closeWithNow(): void {

    this.pickerDate = null;
    this.pickerTime = null;

    this.updateSelection('now');

    this.close();
  }

  toggleVisibility(): void {
    this.picker.toggle();
  }

  private applyDateBoundaries(): void {

    if (this.offerDatesFrom) {
      this.pickerMinDate = this.fromUtcDate(this.offerDatesFrom);
    }

    if (this.offerDatesTo) {
      this.pickerMaxDate = this.fromUtcDate(this.offerDatesTo);
    }
  }

  private setPickerDate(
    selection: DateSelection
  ): void {

    this._selection = selection;

    if (selection instanceof Date) {

      this.pickerDate = this.fromUtcDate(selection);
      this.pickerTime = this.fromUtcDate(selection);

    } else {

      this.pickerDate = null;
      this.pickerTime = null;
    }
  }

  private fromUtcDate(
    date: Date
  ): NgbDateStruct & NgbTimeStruct {

    if (!date) {
      return null;
    }

    const utcDate = DateTime.fromJSDate(
      date,
      { zone: 'utc' }
    );

    const zonedDate = utcDate.setZone(this._timeZone);

    return {
      year: zonedDate.year,
      month: zonedDate.month,
      day: zonedDate.day,
      hour: this.allowTimeComponent ? zonedDate.hour : 0,
      minute: this.allowTimeComponent ? zonedDate.minute : 0,
      second: this.allowTimeComponent ? zonedDate.second : 0,
    };
  }

  private toUtcDate(
    date: NgbDateStruct,
    time: NgbTimeStruct
  ): Date {

    const now = this.nowProvider.now();

    date = date || this.fromUtcDate(now) as NgbDateStruct;
    time = time || this.fromUtcDate(now) as NgbTimeStruct;

    return DateTime.fromObject({
      year: date.year,
      month: date.month,
      day: date.day,
      hour: this.allowTimeComponent ? time.hour : 0,
      minute: this.allowTimeComponent ? time.minute : 0,
      second: this.allowTimeComponent ? time.second : 0,
      zone: this._timeZone,
    }).toJSDate();
  }

  private updateSelection(
    selection: DateSelection
  ): void {

    this._selection = selection;

    this.selected.emit(this._selection);
  }

}
